import { ArrowUpRight, Check } from "lucide-react";
import Faq from "../components/Faq";
import PriceList, { OrderLine } from "../components/PriceList";
import type { Block, InfoPageData } from "../data/infoPages";

const slug = (title: string) => title.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-|-$/g, "");

const ContactButton = () => (
  <a
    href="/contact.html"
    className="inline-flex items-center gap-2 px-8 py-4 bg-linear-to-r/srgb from-teal-500 to-cyan-600 text-white rounded-xl font-medium hover:brightness-110 transition-[filter]"
  >
    Get in Touch
    <ArrowUpRight className="w-5 h-5" />
  </a>
);

const BlockBody = ({ b, plain }: { b: Block; plain?: boolean }) => (
  <>
    {b.body?.map((p) => (
      <p key={p} className="text-gray-400 leading-relaxed mb-4 max-w-3xl">
        {p}
      </p>
    ))}

    {b.highlights && (
      <div className="grid sm:grid-cols-2 gap-4 mb-4">
        {b.highlights.map((h, i) => (
          <div key={h.label} className={`p-6 rounded-2xl glass ${i === 0 ? "border-teal-500/30" : ""}`}>
            <p className={`text-sm mb-2 ${i === 0 ? "text-teal-400" : "text-gray-400"}`}>{h.label}</p>
            <p className="text-3xl font-medium tabular-nums text-white mb-2">{h.price}</p>
            <p className="text-gray-400 text-sm leading-relaxed">{h.desc}</p>
          </div>
        ))}
      </div>
    )}
    {b.order && <OrderLine />}

    {b.plans && (
      <>
        <div className="grid sm:grid-cols-3 gap-4 mb-4">
          {b.plans.map((plan) => (
            <div key={plan.name} className="p-6 rounded-2xl glass">
              <p className="text-lg font-medium text-white mb-1">{plan.name}</p>
              <p className="text-3xl font-medium tabular-nums text-teal-400 mb-3">{plan.price}</p>
              <p className="text-gray-400 text-sm mb-5">{plan.desc}</p>
              <ul className="space-y-2.5">
                {plan.features.map((f) => (
                  <li key={f} className="flex items-start gap-2.5 text-gray-300 text-sm">
                    <Check className="w-4 h-4 text-teal-400 shrink-0 mt-0.5" />
                    {f}
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>
        <OrderLine />
      </>
    )}

    {b.prices && <PriceList rows={b.prices} />}

    {b.cards && (
      <div className="grid sm:grid-cols-2 gap-4">
        {b.cards.map((c) => (
          <div key={c.title} className="p-6 rounded-2xl glass">
            <h3 className="text-white font-medium mb-2">{c.title}</h3>
            <p className="text-gray-400 text-sm leading-relaxed">{c.desc}</p>
          </div>
        ))}
      </div>
    )}

    {b.steps && (
      <div className="grid sm:grid-cols-3 gap-4">
        {b.steps.map((s, i) => (
          <div key={s.title} className="p-6 rounded-2xl glass">
            <p className="text-teal-400 font-medium tabular-nums mb-3">{i + 1}</p>
            <h3 className="text-white font-medium mb-2">{s.title}</h3>
            <p className="text-gray-400 text-sm leading-relaxed">{s.desc}</p>
          </div>
        ))}
      </div>
    )}

    {b.checklist &&
      (plain ? (
        <ul className="list-disc pl-5 space-y-2 text-gray-400 leading-relaxed max-w-3xl">
          {b.checklist.map((item) => (
            <li key={item}>{item}</li>
          ))}
        </ul>
      ) : (
        <ul className="grid gap-3">
          {b.checklist.map((item) => (
            <li key={item} className="flex items-start gap-3 text-gray-300 leading-relaxed">
              <Check className="w-4 h-4 text-teal-400 shrink-0 mt-1" />
              {item}
            </li>
          ))}
        </ul>
      ))}

    {b.sites && (
      <div className="grid sm:grid-cols-2 gap-6">
        {b.sites.map((s) => (
          <a
            key={s.name}
            href={s.url}
            target="_blank"
            rel="noopener noreferrer"
            className="group block rounded-2xl glass overflow-hidden hover:border-teal-500/50 transition-colors"
          >
            {s.image && (
              <img
                src={s.image}
                alt={`${s.name} website homepage`}
                width={1280}
                height={800}
                loading="lazy"
                className="w-full aspect-16/10 object-cover object-top border-b border-white/10"
              />
            )}
            <div className="p-6">
              <div className="flex items-start justify-between mb-1">
                <h3 className="text-white font-medium text-lg">{s.name}</h3>
                <ArrowUpRight className="w-4 h-4 text-gray-400 group-hover:text-teal-400 transition-colors" />
              </div>
              <p className="text-gray-400 text-sm mb-3">{s.trade}</p>
              <p className="text-gray-400 text-sm leading-relaxed">{s.detail}</p>
            </div>
          </a>
        ))}
      </div>
    )}

    {b.links && (
      <div className="flex flex-wrap gap-3">
        {b.links.map((l) => (
          <a
            key={l.href}
            href={l.href}
            className="px-4 py-2 rounded-xl glass hover:border-teal-500/50 text-gray-300 hover:text-white text-sm transition-colors"
          >
            {l.label}
          </a>
        ))}
      </div>
    )}

    {b.callout && (
      <a
        href={b.callout.href}
        className="group flex items-center justify-between gap-6 p-6 rounded-2xl glass border-teal-500/30 hover:border-teal-500/60 transition-colors"
      >
        <span className="text-gray-300 leading-relaxed">{b.callout.text}</span>
        <span className="flex items-center gap-1.5 shrink-0 text-teal-400 text-sm font-medium">
          {b.callout.label}
          <ArrowUpRight className="w-4 h-4" />
        </span>
      </a>
    )}
  </>
);

// The one template for every service, trade, town, question and legal page, so they read as one company.
const InfoPage = ({ data }: { data: InfoPageData }) => (
  <section className="relative min-h-screen pt-32 pb-24">
    <div className="relative max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
      <div className={data.jump ? "mb-8" : "mb-16"}>
        <h1 className="text-4xl sm:text-5xl lg:text-6xl font-semibold tracking-tight text-white mb-4">{data.h1}</h1>
        <p className="text-xl text-gray-400 max-w-2xl">{data.lead}</p>
        {data.lead2 && <p className="text-gray-400 max-w-2xl mt-4">{data.lead2}</p>}
      </div>

      {data.jump && (
        <nav aria-label="On this page" className="flex flex-wrap gap-2 mb-16">
          {data.blocks.map((b) => (
            <a
              key={b.title}
              href={`#${slug(b.title)}`}
              className="px-3 py-1.5 rounded-lg border border-white/10 text-sm text-gray-400 hover:text-white hover:border-teal-500/50 transition-colors"
            >
              {b.title}
            </a>
          ))}
        </nav>
      )}

      {data.blocks.map((b, i) =>
        b.title ? (
          <div key={b.title} id={slug(b.title)} className={`scroll-mt-28 ${data.plain ? "mb-10" : "mb-16"}`}>
            <h2 className={`font-semibold text-white ${data.plain ? "text-xl mb-3" : "text-2xl mb-6"}`}>
              {data.plain ? `${i + 1}. ${b.title}` : b.title}
            </h2>
            <BlockBody b={b} plain={data.plain} />
          </div>
        ) : (
          <div key={i} className="mb-16">
            <BlockBody b={b} />
          </div>
        ),
      )}

      {data.faqs.length > 0 && <Faq items={data.faqs} />}

      <div className="p-8 rounded-2xl glass text-center">
        <h2 className="text-2xl font-semibold text-white mb-3">{data.ctaTitle}</h2>
        <p className="text-gray-400 mb-6">{data.ctaText}</p>
        <ContactButton />
      </div>
    </div>
  </section>
);

export default InfoPage;
